/**
 * CSV parsing utilities for Upload Multiple Sources functionality
 * These replicate the logic from Backbone's assets_upload_module.js
 */

/**
 * Parse Windows domain logon format (domain\username or username@domain)
 * Replicates RM.parseWindowsLogon from Backbone helpers
 */
export const parseWindowsLogon = (username) => {
    const result = { username: username };

    // Handle domain\username format
    if (username.includes('\\')) {
        const parts = username.split('\\');
        if (parts.length === 2) {
            result.domain = parts[0];
            result.username = parts[1];
        }
    }
    // Handle username@domain format
    else if (username.includes('@')) {
        const parts = username.split('@');
        if (parts.length === 2) {
            result.username = parts[0];
            result.domain = parts[1];
        }
    }

    return result;
};

export const parseCSV = (csvText) => {
    const lines = csvText.split('\n').filter((line) => line.trim() !== '');
    if (lines.length === 0) return { columns: [], data: [] };

    // Table Headers
    const headers = lines[0].split(',').map((header) => header.trim());

    // Data rows
    const data = lines.slice(1).map((line, index) => {
        const values = parseCSVLine(line);
        const row = { key: index };
        headers.forEach((header, i) => {
            row[header] = values[i] || '';
        });
        return row;
    });

    const headerCellStyle = { whiteSpace: 'nowrap' };
    const cellStyle = { whiteSpace: 'nowrap' };

    // Create columns for table
    const columns = headers.map((header) => {
        // Calculate header width. 10px per character + padding
        const headerWidth = header.length * 10 + 32;

        // Calculate max content width for a given column
        const maxContentLength = data.reduce((max, row) => {
            const cellLength = String(row[header] || '').trim();
            return Math.max(max, cellLength.length);
        }, 0);

        // Set content width. 10px per character + padding
        const contentWidth = maxContentLength * 10 + 32;

        // Use the larger of headerWidth and contentWidth, with a max width of 300px
        const width = Math.min(Math.max(headerWidth, contentWidth), 300);

        return {
            title: header,
            dataIndex: header,
            key: header,
            ellipsis: true,
            width: width,
            onHeaderCell: () => ({ style: headerCellStyle }),
            onCell: () => ({ style: cellStyle }),
        };
    });

    return { columns, data };
};

/**
 * Simple CSV parser that mimics jQuery CSV behavior
 * Handles quoted fields, escaped quotes, and proper comma separation
 */
export const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];

        if (char === '"' && inQuotes && nextChar === '"') {
            // Escaped quote
            current += '"';
            i++; // Skip next quote
        } else if (char === '"') {
            inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
            result.push(current.trim());
            current = '';
        } else {
            current += char;
        }
    }

    result.push(current.trim());
    return result;
};

/**
 * Parse CSV text into arrays, replicating jQuery CSV toArrays behavior
 * Includes line normalization from Backbone implementation
 */
export const parseCSVToArrays = (csvText) => {
    // Convert all new lines to unix line breaks and remove multiple newlines
    // This replicates the Backbone logic from RM-2469 and RM-7598
    const normalizedCsv = csvText
        .replace(/(?:\r\n|\r|$)/g, '\n') // Convert line endings
        .replace(/\n{2,}/g, '\n'); // Remove multiple newlines

    const lines = normalizedCsv.split('\n').filter((line) => line.trim());

    if (lines.length === 0) {
        throw new Error('Empty CSV file');
    }

    const arrays = [];

    for (const line of lines) {
        if (line.trim()) {
            arrays.push(parseCSVLine(line));
        }
    }

    return arrays;
};

/**
 * Validate and parse a source object from CSV row data
 * Replicates the Backbone validation and parsing logic
 */
export const parseSourceFromCSVRow = (headerRow, row, filename, index) => {
    const source = {
        host: '',
        source_ip: '',
        name: '',
        description: '',
        os_type: 'linux',
        ssh_port: 22,
        tags: [],
        credentials: {
            username: '',
            password: '',
            private_key: '',
            storage: 'ca',
        },
    };

    // Map CSV columns to source properties
    for (let x = 0; x < row.length; x++) {
        const header = headerRow[x];
        const value = row[x] || '';

        if (header === 'username') {
            // Parse Windows domain logon format
            const parsed = parseWindowsLogon(value);
            Object.assign(source.credentials, parsed);
        } else if (header === 'password') {
            source.credentials.password = value;
        } else if (header === 'private_key') {
            source.credentials.private_key = value;
        } else if (header === 'tags') {
            if (value.length > 0) {
                // Use comma delimiter and filter empty entries (Backbone behavior)
                const tagsList = value
                    .split(',')
                    .map((tag) => tag.trim())
                    .filter((tag) => tag);
                source.tags = tagsList;
            }
        } else if (header === 'advanced_instructions') {
            if (value.trim() !== '') {
                const instructions_list = value.split(';').map((val) => val.trim());
                const instructions = {};

                instructions_list.forEach((instruction_string) => {
                    const instruction_array = instruction_string.split('=').map((val) => val.trim());
                    if (instruction_array.length === 2) {
                        let instruction_value = instruction_array[1];
                        switch (instruction_value.trim().toLowerCase()) {
                            case 'true':
                                instruction_value = true;
                                break;
                            case 'false':
                                instruction_value = false;
                                break;
                        }
                        instructions[instruction_array[0]] = instruction_value;
                    }
                });
                source.instructions = instructions;
            }
        } else if (header === 'ssh_port' || header === 'port') {
            if (value) {
                source.ssh_port = parseInt(value) || 22;
            }
        } else if (header === 'os_type' || header === 'os') {
            if (value) {
                source.os_type = value.toLowerCase();
            }
        } else {
            // Map any other headers directly to the source object (Backbone behavior)
            source[header] = value;
        }
    }

    // Handle source_ip to host mapping (Backbone compatibility)
    if (source.source_ip) {
        source.host = source.source_ip;
    }

    // Set name if not provided (Backbone logic)
    if (!source.name) {
        if (source.host) {
            source.name = source.host;
        } else {
            source.name = `${filename} - Source ${index + 1}`;
        }
    }

    return source;
};

/**
 * Set credentials storage type based on provided credentials
 * Replicates Backbone setCredentialsStorage logic
 */
export const setCredentialsStorage = (source) => {
    if (source.credentials.username && (source.credentials.password || source.credentials.private_key)) {
        source.credentials.storage = 'local';
    } else {
        source.credentials.storage = 'ca';
    }
    return source;
};
